/* =============================================
   SECTION: Customer Reviews — JavaScript
   Carrossel nativo (scroll-snap) + IntersectionObserver
   Zero dependências externas
   ============================================= */

if (!customElements.get('reviews-carousel')) {
  class ReviewsCarousel extends HTMLElement {
    constructor() {
      super();
      this.track = this.querySelector('[data-reviews-track]');
      this.cards = this.track ? [...this.track.children] : [];
      this.prevBtn = this.querySelector('[data-reviews-prev]');
      this.nextBtn = this.querySelector('[data-reviews-next]');
      this.dotsContainer = this.querySelector('[data-reviews-dots]');
      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 5000;
      this.autoplayTimer = null;
      this.currentPage = 0;
    }

    connectedCallback() {
      if (!this.track || this.cards.length === 0) return;

      this.#bindArrows();
      this.#buildDots();
      this.#observeScroll();
      this.#updateArrows();

      if (this.autoplay && this.cards.length > 1) {
        this.#startAutoplay();
        this.addEventListener('mouseenter', () => this.#stopAutoplay());
        this.addEventListener('mouseleave', () => this.#startAutoplay());
        this.addEventListener('focusin', () => this.#stopAutoplay());
        this.addEventListener('focusout', () => this.#startAutoplay());
      }
    }

    disconnectedCallback() {
      this.#stopAutoplay();
    }

    #bindArrows() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.#scroll(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.#scroll(1));
      }
    }

    #scroll(direction) {
      if (!this.track) return;
      const card = this.cards[0];
      if (!card) return;
      const gap = parseInt(getComputedStyle(this.track).gap, 10) || 0;
      const scrollAmount = (card.offsetWidth + gap) * direction;
      this.track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }

    #observeScroll() {
      let ticking = false;
      this.track.addEventListener('scroll', () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            this.#updateArrows();
            this.#updateDots();
            this.#updateCardVisibility();
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });

      /* Atualiza visibilidade inicial */
      this.#updateCardVisibility();
    }

    #updateCardVisibility() {
      if (!this.track || this.cards.length === 0) return;
      const trackRect = this.track.getBoundingClientRect();
      const threshold = 0.5; /* Card precisa estar 50% visível pra ser "active" */

      this.cards.forEach((card) => {
        const cardRect = card.getBoundingClientRect();
        const cardLeft = cardRect.left - trackRect.left;
        const cardRight = cardRect.right - trackRect.left;
        const trackWidth = trackRect.width;
        const visibleLeft = Math.max(0, cardLeft);
        const visibleRight = Math.min(trackWidth, cardRight);
        const visibleWidth = Math.max(0, visibleRight - visibleLeft);
        const ratio = visibleWidth / cardRect.width;

        card.classList.remove('reviews-card--active', 'reviews-card--out-left', 'reviews-card--out-right');

        if (ratio >= threshold) {
          card.classList.add('reviews-card--active');
        } else if (cardRight <= trackWidth * 0.5) {
          card.classList.add('reviews-card--out-left');
        } else {
          card.classList.add('reviews-card--out-right');
        }
      });
    }

    #updateArrows() {
      if (!this.track) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;

      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;
    }

    #buildDots() {
      if (!this.dotsContainer || !this.track) return;
      const visibleWidth = this.track.clientWidth;
      const card = this.cards[0];
      if (!card) return;
      const gap = parseInt(getComputedStyle(this.track).gap, 10) || 0;
      const cardWidth = card.offsetWidth + gap;
      const perPage = Math.round(visibleWidth / cardWidth) || 1;
      const totalPages = Math.ceil(this.cards.length / perPage);

      if (totalPages <= 1) {
        this.dotsContainer.style.display = 'none';
        return;
      }

      this.dotsContainer.innerHTML = '';
      for (let i = 0; i < totalPages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'reviews__dot' + (i === 0 ? ' reviews__dot--active' : '');
        dot.setAttribute('aria-label', `Page ${i + 1} of ${totalPages}`);
        dot.addEventListener('click', () => {
          const scrollTo = i * perPage * cardWidth;
          this.track.scrollTo({ left: scrollTo, behavior: 'smooth' });
        });
        this.dotsContainer.appendChild(dot);
      }
    }

    #updateDots() {
      if (!this.dotsContainer || !this.track) return;
      const dots = this.dotsContainer.querySelectorAll('.reviews__dot');
      if (dots.length === 0) return;
      const card = this.cards[0];
      if (!card) return;
      const gap = parseInt(getComputedStyle(this.track).gap, 10) || 0;
      const cardWidth = card.offsetWidth + gap;
      const perPage = Math.round(this.track.clientWidth / cardWidth) || 1;
      const page = Math.round(this.track.scrollLeft / (cardWidth * perPage));
      dots.forEach((d, i) => d.classList.toggle('reviews__dot--active', i === page));
      this.currentPage = page;
    }

    #startAutoplay() {
      this.#stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        if (!this.track) return;
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        if (scrollLeft + clientWidth >= scrollWidth - 2) {
          this.track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.#scroll(1);
        }
      }, this.autoplaySpeed);
    }

    #stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }
  }

  customElements.define('reviews-carousel', ReviewsCarousel);
}

/* --- IntersectionObserver para animações dos cards --- */
(function () {
  const cards = document.querySelectorAll('.reviews__card');
  if (!cards.length) return;

  if (!('IntersectionObserver' in window)) {
    cards.forEach((c) => c.classList.add('reviews--visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('reviews--visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.15 }
  );

  cards.forEach((card) => observer.observe(card));
})();
